import { ArrowLeft01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useEffect, useMemo, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { statusBadgeVariant } from "@/lib/case-format";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  api,
  ApiError,
  type LlmCall,
  type LlmCallDetail,
  type PipelineStep,
} from "@/lib/api";
import { formatDurationMs } from "@/lib/utils";

type Props = {
  callId: string | null;
  /** All LLM calls attached to the inspected step (multi-call steps). */
  stepCalls?: LlmCall[];
  /** True while the step-scoped call list is loading. */
  stepCallsLoading?: boolean;
  /** Pipeline step metadata when opened from a step row. */
  step?: PipelineStep | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Live-tailed text for the open call (from version WebSocket). */
  liveText?: string;
  onRerun?: (newCall: LlmCall) => void;
  /** Select a call for detail, or null to return to the step overview. */
  onSelectCall?: (callId: string | null) => void;
};

function formatUsd(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "-";
  if (n === 0) return "$0";
  if (n < 0.0001) return `$${n.toExponential(2)}`;
  return `$${n.toFixed(6)}`;
}

function pretty(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("zh-CN", {
      dateStyle: "medium",
      timeStyle: "medium",
    });
  } catch {
    return iso;
  }
}

function usageNum(call: LlmCall, key: string): number | null {
  const v = call.usage?.[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function usageNumLabel(call: LlmCall, key: string): string {
  const v = usageNum(call, key);
  return v == null ? "-" : String(v);
}

export function LlmCallSheet({
  callId,
  stepCalls = [],
  stepCallsLoading = false,
  step = null,
  open,
  onOpenChange,
  liveText,
  onRerun,
  onSelectCall,
}: Props) {
  const [detail, setDetail] = useState<LlmCallDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [rerunning, setRerunning] = useState(false);

  const orderedStepCalls = useMemo(() => {
    if (stepCalls.length === 0) return [];
    return [...stepCalls].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }, [stepCalls]);

  const callIndex = useMemo(() => {
    if (!callId) return -1;
    return orderedStepCalls.findIndex((c) => c.id === callId);
  }, [callId, orderedStepCalls]);

  const callTotals = useMemo(() => {
    let costUsd = 0;
    let hasCost = false;
    let inputTokens = 0;
    let outputTokens = 0;
    let hasInput = false;
    let hasOutput = false;
    let durationMs = 0;
    let hasDuration = false;
    let succeeded = 0;
    let failed = 0;
    let running = 0;
    for (const c of orderedStepCalls) {
      if (c.costUsd != null && Number.isFinite(c.costUsd)) {
        costUsd += c.costUsd;
        hasCost = true;
      }
      if (c.durationMs != null && Number.isFinite(c.durationMs)) {
        durationMs += c.durationMs;
        hasDuration = true;
      }
      const inTok = usageNum(c, "input_tokens");
      if (inTok != null) {
        inputTokens += inTok;
        hasInput = true;
      }
      const outTok = usageNum(c, "output_tokens");
      if (outTok != null) {
        outputTokens += outTok;
        hasOutput = true;
      }
      if (c.status === "succeeded") succeeded += 1;
      else if (c.status === "failed") failed += 1;
      else if (c.status === "running") running += 1;
    }
    return {
      count: orderedStepCalls.length,
      costUsd: hasCost ? costUsd : null,
      inputTokens: hasInput ? inputTokens : null,
      outputTokens: hasOutput ? outputTokens : null,
      durationMs: hasDuration ? durationMs : null,
      succeeded,
      failed,
      running,
    };
  }, [orderedStepCalls]);

  // Overview when no call selected; detail when a call is open.
  const view: "overview" | "detail" = callId ? "detail" : "overview";

  useEffect(() => {
    if (!open || !callId || view !== "detail") {
      if (view !== "detail") {
        setDetail(null);
        setError(null);
        setSystemPrompt("");
      }
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getLlmCall(callId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setSystemPrompt(d.call.systemPrompt ?? "");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.code : "load_failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, callId, view]);

  useEffect(() => {
    if (!open || !callId || view !== "detail" || !detail) return;
    if (detail.call.status !== "running") return;
    const t = setInterval(() => {
      api
        .getLlmCall(callId)
        .then((d) => {
          setDetail(d);
          if (d.call.systemPrompt != null) {
            setSystemPrompt(d.call.systemPrompt);
          }
        })
        .catch(() => {
          /* ignore poll errors */
        });
    }, 1500);
    return () => clearInterval(t);
  }, [open, callId, view, detail?.call.status]);

  const call = detail?.call;
  const streamText =
    liveText ||
    detail?.streamTranscript ||
    (detail?.output &&
    typeof detail.output === "object" &&
    detail.output !== null &&
    "text" in detail.output &&
    typeof (detail.output as { text?: unknown }).text === "string"
      ? (detail.output as { text: string }).text
      : "") ||
    "";

  const onRerunClick = async () => {
    if (!callId || rerunning) return;
    setRerunning(true);
    setError(null);
    try {
      const res = await api.rerunLlmCall(callId, {
        systemPrompt: systemPrompt.trim() || undefined,
      });
      onRerun?.(res.call);
    } catch (err) {
      setError(err instanceof ApiError ? err.code : "rerun_failed");
    } finally {
      setRerunning(false);
    }
  };

  const title =
    view === "detail"
      ? callIndex >= 0
        ? `调用 #${callIndex + 1}`
        : call
          ? "LLM 调用"
          : "调用详情"
      : step
        ? `步骤：${step.stepKey}`
        : "步骤详情";

  const description =
    view === "detail"
      ? "单次 LLM 调用的指标、日志与原始请求 / 响应。"
      : "步骤汇总及本步骤上的全部 LLM 调用（含重试与多调用运行）。";

  const usageEntries = useMemo((): Array<[string, string]> => {
    if (!call?.usage) return [];
    return Object.entries(call.usage).map(([key, value]): [string, string] => [
      key,
      typeof value === "number" || typeof value === "string"
        ? String(value)
        : pretty(value),
    ]);
  }, [call?.usage]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-3/4 !max-w-[75vw] flex-col overflow-hidden sm:!max-w-[75vw] data-[side=right]:w-3/4 data-[side=right]:!max-w-[75vw] data-[side=right]:sm:!max-w-[75vw]"
      >
        <SheetHeader>
          <div className="flex items-start gap-2 pr-8">
            {view === "detail" && step ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="mt-0.5 shrink-0"
                onClick={() => onSelectCall?.(null)}
                aria-label="返回步骤总览"
              >
                <HugeiconsIcon icon={ArrowLeft01Icon} strokeWidth={2} />
              </Button>
            ) : null}
            <div className="flex min-w-0 flex-col gap-1.5">
              <SheetTitle>{title}</SheetTitle>
              <SheetDescription>{description}</SheetDescription>
              {view === "detail" && step ? (
                <p className="text-[0.65rem] text-muted-foreground">
                  步骤{" "}
                  <span className="font-medium text-foreground">
                    {step.stepKey}
                  </span>
                  {callIndex >= 0
                    ? ` · ${callIndex + 1} / ${orderedStepCalls.length}`
                    : null}
                </p>
              ) : null}
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="min-h-0 flex-1 px-6">
          <div className="flex flex-col gap-5 pb-4">
            {view === "overview" ? (
              <OverviewBody
                step={step}
                orderedStepCalls={orderedStepCalls}
                callTotals={callTotals}
                stepCallsLoading={stepCallsLoading}
                onSelectCall={onSelectCall}
              />
            ) : stepCallsLoading && !callId ? (
              <div className="flex items-center justify-center py-10">
                <Spinner />
              </div>
            ) : !callId ? (
              <Alert>
                <AlertDescription>
                  未选择 LLM 调用。请返回步骤总览并选择一行。
                </AlertDescription>
              </Alert>
            ) : loading && !detail ? (
              <div className="flex items-center justify-center py-10">
                <Spinner />
              </div>
            ) : error && !detail ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : call ? (
              <CallDetailBody
                call={call}
                detail={detail}
                streamText={streamText}
                systemPrompt={systemPrompt}
                onSystemPromptChange={setSystemPrompt}
                usageEntries={usageEntries}
                error={error}
              />
            ) : null}
          </div>
        </ScrollArea>

        {view === "detail" ? (
          <SheetFooter>
            <Button
              variant="outline"
              onClick={() => void onRerunClick()}
              disabled={!call || rerunning}
            >
              {rerunning ? <Spinner /> : null}
              {rerunning
                ? "重跑中…"
                : call?.status === "failed"
                  ? "重试"
                  : call?.status === "succeeded" && call.stepId
                    ? "重跑（分叉步骤）"
                    : "重跑"}
            </Button>
          </SheetFooter>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function OverviewBody({
  step,
  orderedStepCalls,
  callTotals,
  stepCallsLoading,
  onSelectCall,
}: {
  step: PipelineStep | null;
  orderedStepCalls: LlmCall[];
  callTotals: {
    count: number;
    costUsd: number | null;
    inputTokens: number | null;
    outputTokens: number | null;
    durationMs: number | null;
    succeeded: number;
    failed: number;
    running: number;
  };
  stepCallsLoading: boolean;
  onSelectCall?: (callId: string | null) => void;
}) {
  return (
    <>
      {step ? (
        <section className="flex flex-col gap-2">
          <p className="text-xs font-medium text-muted-foreground">
            步骤摘要
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={statusBadgeVariant(step.status)}>
              {step.status}
            </Badge>
            <code className="text-[0.65rem] text-muted-foreground">
              {step.id}
            </code>
            {step.attemptN > 1 ? (
              <span className="text-xs text-muted-foreground">
                第 {step.attemptN} 次尝试
              </span>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="步骤模型" value={step.model ?? "-"} />
            <Stat label="步骤费用" value={formatUsd(step.costUsd)} />
            <Stat
              label="步骤耗时"
              value={
                step.durationMs != null
                  ? formatDurationMs(step.durationMs)
                  : "-"
              }
              tip={
                step.durationMs != null ? `${step.durationMs} ms` : undefined
              }
            />
            <Stat label="更新时间" value={formatWhen(step.updatedAt)} />
          </div>
          {step.errorText ? (
            <Alert variant="destructive">
              <AlertDescription>{step.errorText}</AlertDescription>
            </Alert>
          ) : null}
        </section>
      ) : null}

      <section className="flex flex-col gap-2">
        <p className="text-xs font-medium text-muted-foreground">
          LLM 调用合计
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="调用次数" value={String(callTotals.count)} />
          <Stat label="总费用" value={formatUsd(callTotals.costUsd)} />
          <Stat
            label="输入 token"
            value={
              callTotals.inputTokens != null
                ? String(callTotals.inputTokens)
                : "-"
            }
          />
          <Stat
            label="输出 token"
            value={
              callTotals.outputTokens != null
                ? String(callTotals.outputTokens)
                : "-"
            }
          />
          <Stat
            label="总耗时"
            value={
              callTotals.durationMs != null
                ? formatDurationMs(callTotals.durationMs)
                : "-"
            }
            tip={
              callTotals.durationMs != null
                ? `${callTotals.durationMs} ms`
                : undefined
            }
          />
          <Stat label="成功" value={String(callTotals.succeeded)} />
          <Stat label="失败" value={String(callTotals.failed)} />
          <Stat label="运行中" value={String(callTotals.running)} />
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <p className="text-xs font-medium text-muted-foreground">
          本步骤的调用
        </p>
        {stepCallsLoading && orderedStepCalls.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <Spinner />
          </div>
        ) : orderedStepCalls.length === 0 ? (
          <Alert>
            <AlertDescription>
              本步骤没有关联的 LLM 调用。上方指标仅来自流水线步骤行。
            </AlertDescription>
          </Alert>
        ) : (
          <div className="rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>模型</TableHead>
                  <TableHead>费用</TableHead>
                  <TableHead>耗时</TableHead>
                  <TableHead>入 / 出</TableHead>
                  <TableHead>创建时间</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {orderedStepCalls.map((c, index) => (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer"
                    onClick={() => onSelectCall?.(c.id)}
                  >
                    <TableCell className="text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(c.status)}>
                        {c.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[12rem] truncate">
                      <span title={c.model}>
                        {c.productName ?? c.model}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-[0.65rem]">
                      {formatUsd(c.costUsd)}
                    </TableCell>
                    <TableCell className="font-mono text-[0.65rem]">
                      {c.durationMs != null
                        ? formatDurationMs(c.durationMs)
                        : "-"}
                    </TableCell>
                    <TableCell className="font-mono text-[0.65rem]">
                      {usageNumLabel(c, "input_tokens")} /{" "}
                      {usageNumLabel(c, "output_tokens")}
                    </TableCell>
                    <TableCell className="text-[0.65rem] text-muted-foreground">
                      {formatWhen(c.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-7 px-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectCall?.(c.id);
                        }}
                      >
                        打开
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </>
  );
}

function CallDetailBody({
  call,
  detail,
  streamText,
  systemPrompt,
  onSystemPromptChange,
  usageEntries,
  error,
}: {
  call: LlmCall;
  detail: LlmCallDetail | null;
  streamText: string;
  systemPrompt: string;
  onSystemPromptChange: (value: string) => void;
  usageEntries: Array<[string, string]>;
  error: string | null;
}) {
  return (
    <>
      <section className="flex flex-col gap-2">
        <p className="text-xs font-medium text-muted-foreground">LLM 调用</p>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={statusBadgeVariant(call.status)}>
            {call.status}
          </Badge>
          <span className="text-muted-foreground">
            {call.productName ?? call.model}
          </span>
          <code className="text-[0.65rem] text-muted-foreground">
            {call.model}
          </code>
          <code className="text-[0.65rem] text-muted-foreground">{call.id}</code>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="输入 token" value={usageNumLabel(call, "input_tokens")} />
          <Stat
            label="输出 token"
            value={usageNumLabel(call, "output_tokens")}
          />
          <Stat label="费用" value={formatUsd(call.costUsd)} />
          <Stat
            label="耗时"
            value={
              call.durationMs != null
                ? formatDurationMs(call.durationMs)
                : "-"
            }
            tip={
              call.durationMs != null ? `${call.durationMs} ms` : undefined
            }
          />
          <Stat label="创建时间" value={formatWhen(call.createdAt)} />
          <Stat label="结束时间" value={formatWhen(call.finishedAt)} />
          <Stat
            label="资源"
            value={call.assetId ? call.assetId.slice(0, 8) + "…" : "-"}
            tip={call.assetId ?? undefined}
          />
          <Stat
            label="Agent 会话"
            value={
              call.agentSessionId
                ? call.agentSessionId.slice(0, 8) + "…"
                : "-"
            }
            tip={call.agentSessionId ?? undefined}
          />
        </div>

        {usageEntries.length > 0 ? (
          <div className="flex flex-col gap-1">
            <span className="text-[0.65rem] text-muted-foreground">
              完整用量
            </span>
            <pre className="max-h-40 overflow-auto rounded-md bg-muted p-2 font-mono text-[0.65rem] whitespace-pre-wrap">
              {pretty(call.usage)}
            </pre>
          </div>
        ) : null}

        {call.errorText ? (
          <Alert variant="destructive">
            <AlertDescription>{call.errorText}</AlertDescription>
          </Alert>
        ) : null}

        <div className="grid gap-1 text-[0.65rem] text-muted-foreground">
          <div>
            input r2:{" "}
            <code className="break-all">{call.inputR2Key ?? "-"}</code>
          </div>
          <div>
            output r2:{" "}
            <code className="break-all">{call.outputR2Key ?? "-"}</code>
          </div>
          <div>
            stream r2:{" "}
            <code className="break-all">{call.streamR2Key ?? "-"}</code>
          </div>
        </div>
      </section>

      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="llm-system">系统提示词</FieldLabel>
          <Textarea
            id="llm-system"
            value={systemPrompt}
            onChange={(e) => onSystemPromptChange(e.target.value)}
            rows={4}
            className="max-h-48 overflow-y-auto font-mono text-xs"
          />
        </Field>
      </FieldGroup>

      <section className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">
          原始请求（input）
        </span>
        <pre className="max-h-[40vh] overflow-auto rounded-md bg-muted p-3 font-mono text-[0.65rem] whitespace-pre-wrap">
          {pretty(detail?.input) || (
            <span className="text-muted-foreground">空</span>
          )}
        </pre>
      </section>

      <section className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">
          流式转录
        </span>
        <pre className="max-h-[30vh] overflow-auto rounded-md bg-muted p-3 font-mono text-[0.65rem] whitespace-pre-wrap">
          {streamText || (
            <span className="text-muted-foreground">
              {call.status === "running" ? "等待中…" : "空"}
            </span>
          )}
        </pre>
      </section>

      <section className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">
          原始响应（output）
        </span>
        <pre className="max-h-[40vh] overflow-auto rounded-md bg-muted p-3 font-mono text-[0.65rem] whitespace-pre-wrap">
          {pretty(detail?.output) || (
            <span className="text-muted-foreground">
              {call.status === "running" ? "等待中…" : "空"}
            </span>
          )}
        </pre>
      </section>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
    </>
  );
}

function Stat({
  label,
  value,
  tip,
}: {
  label: string;
  value: string;
  tip?: string;
}) {
  const body = (
    <div className="flex flex-col gap-0.5 rounded-md bg-muted/50 px-2 py-1.5">
      <span className="text-[0.65rem] text-muted-foreground">{label}</span>
      <span className="font-mono text-xs break-all">{value}</span>
    </div>
  );

  if (!tip) return body;

  return (
    <Tooltip>
      <TooltipTrigger render={<div className="cursor-default" />}>
        {body}
      </TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
}
